// src/utils/notificationManager.js
import { PushNotifications } from '@capacitor/push-notifications';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { app, db, doc, updateDoc } from '../firebase';
import { DEFAULT_APP_VERSION } from './versionUtils';

const messaging = getMessaging(app);

// Enhanced logging function for Android
const log = (message, type = 'info') => {
  const timestamp = new Date().toISOString();
  const logMessage = `[FCM-${type.toUpperCase()}] ${timestamp}: ${message}`;
  
  // Console log (for web debugging)
  console.log(logMessage);
  
  // For Android, also log to window for better visibility
  if (window.Capacitor && window.Capacitor.isNativePlatform()) {
    // This will show up in Android logs
    console.error(`[YME-FCM] ${message}`); // Using console.error to make it more visible
  }
};

export const requestNotificationPermission = async () => {
  try {
    log('Requesting notification permission...', 'info');
    
    // Check if we're in a native platform (Android/iOS)
    if (window.Capacitor && window.Capacitor.isNativePlatform()) {
      log('Native platform detected - requesting push notification permissions', 'info');
      const result = await PushNotifications.requestPermissions();
      log(`Push permission result: ${JSON.stringify(result)}`, 'info');
      return result.receive === 'granted';
    }
    
    // For web platform, request permission normally
    const permission = await Notification.requestPermission();
    log(`Web platform - notification permission: ${permission}`, 'info');
    return permission === 'granted';
  } catch (error) {
    log(`Error requesting permission: ${error.message}`, 'error');
    return false;
  }
};

export const getFCMToken = async (forceRefresh = false) => {
  try {
    log('Getting FCM token...', 'info');
    
    // Ensure service worker is registered for web platform
    if (!window.Capacitor && 'serviceWorker' in navigator && navigator.serviceWorker.controller) {
      log('Web platform - service worker already registered', 'info');
    } else if (!window.Capacitor && 'serviceWorker' in navigator) {
      log('Web platform - registering service worker...', 'info');
      try {
        await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        log('Service worker registered successfully', 'success');
      } catch (error) {
        log(`Service worker registration error: ${error.message}`, 'error');
      }
    }
    
    // Check if we're in a Capacitor environment (Android/iOS)
    if (window.Capacitor && window.Capacitor.isNativePlatform()) {
      log('Native platform detected - registering for push notifications', 'info');
      
      // Check if we already have a valid token stored (unless force refresh)
      if (!forceRefresh) {
        const existingToken = localStorage.getItem('fcmToken');
        const tokenTimestamp = localStorage.getItem('fcmTokenTimestamp');
        const now = Date.now();
        
        // Messenger-style: If token is less than 2 hours old, use it (more aggressive refresh)
        if (existingToken && tokenTimestamp && (now - parseInt(tokenTimestamp)) < 2 * 60 * 60 * 1000) {
          log(`Using existing FCM token: ${existingToken.substring(0, 20)}...`, 'info');
          return existingToken;
        }
      }
      
      // Clear old token if force refresh
      if (forceRefresh) {
        localStorage.removeItem('fcmToken');
        localStorage.removeItem('fcmTokenTimestamp');
      }
      
      await PushNotifications.register();
      log('Push notification registration initiated', 'info');
      
      // Wait for the token to be generated with better error handling
      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          log('Timeout waiting for FCM token', 'warning');
          resolve(null);
        }, 10000); // Increased timeout
        
        // Listen for the registration event
        const handleRegistration = (token) => {
          clearTimeout(timeout);
          log(`FCM token received: ${token.value.substring(0, 20)}...`, 'success');
          localStorage.setItem('fcmToken', token.value);
          localStorage.setItem('fcmTokenTimestamp', Date.now().toString());
          resolve(token.value);
        };
        
        const handleError = (error) => {
          clearTimeout(timeout);
          log(`FCM token registration error: ${JSON.stringify(error)}`, 'error');
          resolve(null);
        };
        
        PushNotifications.addListener('registration', handleRegistration);
        PushNotifications.addListener('registrationError', handleError);
        
        // Clean up listeners after timeout
        setTimeout(() => {
          PushNotifications.removeListener('registration', handleRegistration);
          PushNotifications.removeListener('registrationError', handleError);
        }, 12000);
      });
    }
    
    // For web platform, get token normally
    log('Web platform - getting FCM token', 'info');
    const token = await getToken(messaging, {
      vapidKey: 'BM_OzWdwOAulcpYS8nEyOM9osvJsAbEraaPhG2K0HWvSld6hoKkdekFdEwz_uPPqLRCVKKrEi1t-TTYrvwREGng'
    });
    log(`Web platform - FCM token received: ${token ? 'YES' : 'NO'}`, 'info');
    
    if (token) {
      localStorage.setItem('fcmToken', token);
      localStorage.setItem('fcmTokenTimestamp', Date.now().toString());
    }
    
    return token;
  } catch (error) {
    log(`Error getting FCM token: ${error.message}`, 'error');
    return null;
  }
};

export const saveFCMToken = async (userId, token) => {
  try {
    log(`Saving FCM token for user: ${userId}, Token: ${token ? 'PRESENT' : 'MISSING'}`, 'info');
    
    // Messenger-style: Store userId for background refresh
    if (window.Capacitor && window.Capacitor.isNativePlatform()) {
      try {
        const { Preferences } = await import('@capacitor/preferences');
        await Preferences.set({
          key: 'userId',
          value: userId
        });
        log(`✅ UserId stored for background refresh: ${userId}`, 'info');
      } catch (error) {
        log(`⚠️ Failed to store userId for background refresh: ${error.message}`, 'warning');
      }
    }
    
    // Use backup wrapper for FCM token update
    const { updateUserProfileWithBackup } = await import('./clientBackupWrapper');
    await updateUserProfileWithBackup(userId, {
      fcmToken: token,
      lastTokenUpdate: new Date()
    }, 'notification_update', {
      field: 'fcm_token',
      tokenPresent: !!token,
      updateTime: new Date().toISOString()
    });
    
    log(`FCM token saved successfully for user: ${userId}`, 'success');
  } catch (error) {
    log(`Error saving FCM token: ${error.message}`, 'error');
  }
};

export const setupNotificationListener = () => {
  log('Setting up notification listener...', 'info');
  
  // Check if we're in a native platform (Android/iOS via Capacitor)
  if (window.Capacitor && window.Capacitor.isNativePlatform()) {
    log('🤖 Native platform detected - using NATIVE notification handling only', 'info');
    
    // Listen for successful registration
    PushNotifications.addListener('registration', (token) => {
      log(`Push registration success, token: ${token.value}`, 'success');
      
      // Try to save the token immediately if user is logged in
      const userSession = localStorage.getItem('userSession');
      if (userSession) {
        const user = JSON.parse(userSession);
        if (user && user.uid) {
          log(`Saving push notification token for user: ${user.uid}`, 'info');
          saveFCMToken(user.uid, token.value);
        }
      } else {
        // User not logged in yet, store token temporarily
        log('User not logged in, storing FCM token temporarily', 'info');
        localStorage.setItem('pendingFCMToken', token.value);
      }
    });

    // Listen for registration errors
    PushNotifications.addListener('registrationError', (error) => {
      log(`Push registration error: ${JSON.stringify(error)}`, 'error');
    });

    // Listen for push notifications received while app is in foreground
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      log(`📬 Native push notification received in foreground: ${JSON.stringify(notification)}`, 'info');
      // Don't do anything here - let native Android handle it
      // This prevents JavaScript from interfering with the FCM service
    });

    // Listen for push notification actions (when user taps notification)
    PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
      log(`👆 Native push notification tapped: ${JSON.stringify(notification)}`, 'info');
      
      // Handle navigation based on notification data
      if (notification.notification && notification.notification.data) {
        const data = notification.notification.data;
        if (data.type === 'tournament-chat' && data.tournamentId && data.matchId) {
          log(`Navigating to tournament chat: ${data.tournamentId}, match: ${data.matchId}`, 'info');
          window.location.href = `/player-tournament?tournamentId=${data.tournamentId}&match=${data.matchId}`;
        } else if (data.type === 'tournament_pairing') {
          log(`Navigating to player tournament page for pairing`, 'info');
          window.location.href = `/player-tournament`;
        }
      }
    });
    
    // 🔥 CRITICAL: Don't set up Firebase web messaging on native platforms!
    log('✅ Native notification setup complete - web messaging DISABLED to prevent conflicts', 'success');
    return; // Exit early to prevent web notification setup
  }
  
  // Only set up web-based notifications on actual web browsers (not native apps)
  log('🌐 Web platform - setting up Firebase web messaging listeners', 'info');
  
  // Listen for foreground messages (web browsers only)
  if (messaging) {
    onMessage(messaging, (payload) => {
      log(`📬 Web message received: ${JSON.stringify(payload)}`, 'info');
      
      // Show browser notification
      if (payload.notification) {
        const notification = new Notification(payload.notification.title, {
          body: payload.notification.body,
          icon: '/ic_notiflogo.png',
          badge: '/ic_notiflogo.png',
          color: '#FF0000',
          backgroundColor: '#FF0000'
        });
        
        // Handle notification click for web platform
        notification.onclick = () => {
          log('Web notification clicked', 'info');
          if (payload.data) {
            const data = payload.data;
            if (data.type === 'tournament-chat' && data.tournamentId && data.matchId) {
              log(`Navigating to tournament chat: ${data.tournamentId}, match: ${data.matchId}`, 'info');
              window.location.href = `/player-tournament?tournamentId=${data.tournamentId}&match=${data.matchId}`;
            } else if (data.type === 'tournament_pairing') {
              log(`Navigating to player tournament page for pairing`, 'info');
              window.location.href = `/player-tournament`;
            }
          }
          notification.close();
        };
      }
    });
  }
};

// Add a function to save pending FCM token when user logs in
export const savePendingFCMToken = async (userId) => {
  const pendingToken = localStorage.getItem('pendingFCMToken');
  if (pendingToken) {
    log(`Saving pending FCM token for user: ${userId}`, 'info');
    await saveFCMToken(userId, pendingToken);
    localStorage.removeItem('pendingFCMToken');
  }
};

// Add a function to check and restore FCM token from localStorage
export const checkStoredFCMToken = async (userId) => {
  try {
    const storedToken = localStorage.getItem('fcmToken');
    if (storedToken && userId) {
      log(`Found stored FCM token, saving for user: ${userId}`, 'info');
      await saveFCMToken(userId, storedToken);
      return storedToken;
    }
  } catch (error) {
    log(`Error checking stored FCM token: ${error.message}`, 'error');
  }
  return null;
};

// Check if app version has changed and reinitialize notifications if needed
export const checkAndReinitializeNotifications = async (userId) => {
  try {
    const currentVersion = DEFAULT_APP_VERSION;
    const storedVersion = localStorage.getItem('appVersion');
    
    log(`Checking app version: current=${currentVersion}, stored=${storedVersion}`, 'info');
    
    // If version has changed or no version is stored, reinitialize notifications
    if (storedVersion !== currentVersion) {
      log('App version changed, reinitializing notifications', 'info');
      
      // Clear old notification data
      localStorage.removeItem('fcmToken');
      localStorage.removeItem('pendingFCMToken');
      localStorage.removeItem('fcmTokenTimestamp');
      
      // Reinitialize notification system
      const hasPermission = await requestNotificationPermission();
      if (hasPermission) {
        const fcmToken = await getFCMToken(true); // Force refresh
        if (fcmToken && userId) {
          await saveFCMToken(userId, fcmToken);
          log('✅ Notifications reinitialized after version change', 'success');
        }
      }
      
      // Store new version
      localStorage.setItem('appVersion', currentVersion);
    } else {
      log('App version unchanged, notifications should be working', 'info');
    }
  } catch (error) {
    log(`Error checking/reinitializing notifications: ${error.message}`, 'error');
  }
};

// Comprehensive notification initialization with retry logic
export const initializeNotifications = async (userId, retryCount = 0) => {
  const maxRetries = 3;
  
  try {
    log(`Initializing notifications for user: ${userId} (attempt ${retryCount + 1}/${maxRetries + 1})`, 'info');
    
    // Step 1: Check permissions
    const hasPermission = await requestNotificationPermission();
    if (!hasPermission) {
      log('❌ Notification permission denied', 'error');
      return { success: false, error: 'Permission denied' };
    }
    
    // Step 2: Setup listeners first
    setupNotificationListener();
    
    // Step 3: Get FCM token
    const fcmToken = await getFCMToken();
    if (!fcmToken) {
      log('❌ Failed to get FCM token', 'error');
      if (retryCount < maxRetries) {
        log(`Retrying in 2 seconds... (${retryCount + 1}/${maxRetries})`, 'info');
        await new Promise(resolve => setTimeout(resolve, 2000));
        return initializeNotifications(userId, retryCount + 1);
      }
      return { success: false, error: 'Failed to get FCM token' };
    }
    
    // Step 4: Save token to database
    await saveFCMToken(userId, fcmToken);
    
    // Step 5: Validate token by checking if it's saved
    const userDoc = await db.collection('users').doc(userId).get();
    const savedToken = userDoc.data()?.fcmToken;
    
    if (savedToken !== fcmToken) {
      log('❌ Token validation failed - saved token does not match', 'error');
      if (retryCount < maxRetries) {
        log(`Retrying token save... (${retryCount + 1}/${maxRetries})`, 'info');
        await new Promise(resolve => setTimeout(resolve, 1000));
        return initializeNotifications(userId, retryCount + 1);
      }
      return { success: false, error: 'Token validation failed' };
    }
    
    log('✅ Notifications initialized successfully', 'success');
    return { success: true, token: fcmToken };
    
  } catch (error) {
    log(`❌ Error initializing notifications: ${error.message}`, 'error');
    if (retryCount < maxRetries) {
      log(`Retrying in 3 seconds... (${retryCount + 1}/${maxRetries})`, 'info');
      await new Promise(resolve => setTimeout(resolve, 3000));
      return initializeNotifications(userId, retryCount + 1);
    }
    return { success: false, error: error.message };
  }
};

// Force refresh notifications (for troubleshooting)
export const forceRefreshNotifications = async (userId) => {
  try {
    log('🔄 Force refreshing notifications...', 'info');
    
    // Clear all stored data
    localStorage.removeItem('fcmToken');
    localStorage.removeItem('pendingFCMToken');
    localStorage.removeItem('fcmTokenTimestamp');
    
    // Reinitialize
    const result = await initializeNotifications(userId);
    
    if (result.success) {
      log('✅ Notifications force refreshed successfully', 'success');
    } else {
      log(`❌ Failed to force refresh notifications: ${result.error}`, 'error');
    }
    
    return result;
  } catch (error) {
    log(`❌ Error force refreshing notifications: ${error.message}`, 'error');
    return { success: false, error: error.message };
  }
};

// Messenger-style: Enhanced token management with aggressive refresh
export const ensureActiveToken = async (userId) => {
  try {
    // Check if token exists and is recent
    const storedToken = localStorage.getItem('fcmToken');
    const tokenAge = Date.now() - parseInt(localStorage.getItem('fcmTokenTimestamp') || '0');
    
    // Messenger-style: Refresh if token is older than 2 hours (more aggressive than current 24h)
    if (!storedToken || tokenAge > 2 * 60 * 60 * 1000) {
      log(`🔄 Refreshing FCM token (age: ${Math.round(tokenAge / 1000 / 60)} minutes)`, 'info');
      const newToken = await getFCMToken(true);
      if (newToken && userId) {
        await saveFCMToken(userId, newToken);
        return newToken;
      }
    }
    
    return storedToken;
  } catch (error) {
    log(`Error ensuring active token: ${error.message}`, 'error');
    return null;
  }
};

// Messenger-style: Validate token on startup and check for server-side invalidation
export const validateTokenOnStartup = async (userId) => {
  try {
    // Check if server marked our token as invalid
    const userDoc = await db.collection('users').doc(userId).get();
    const userData = userDoc.data();
    
    if (userData?.tokenInvalidated) {
      log('🔄 Server marked token as invalid, refreshing...', 'info');
      const newToken = await getFCMToken(true);
      if (newToken) {
        await saveFCMToken(userId, newToken);
        
        // Clear invalidation flag
        await db.collection('users').doc(userId).update({
          tokenInvalidated: false,
          tokenInvalidatedAt: null
        });
        
        return newToken;
      }
    }
    
    return userData?.fcmToken;
  } catch (error) {
    log(`Error validating token on startup: ${error.message}`, 'error');
    return null;
  }
};

// Messenger-style: Enhanced notification initialization
export const initializeMessengerStyleNotifications = async (userId) => {
  try {
    log(`🚀 Initializing Messenger-style notifications for user: ${userId}`, 'info');
    
    // Step 1: Check battery optimization status
    await checkBatteryOptimization();
    
    // Step 2: Check lockscreen notification settings
    await checkLockscreenNotificationSettings();
    
    // Step 3: Validate existing token
    await validateTokenOnStartup(userId);
    
    // Step 4: Ensure we have an active token
    await ensureActiveToken(userId);
    
    // Step 5: Setup listeners
    setupNotificationListener();
    
    // Step 6: Setup additional listeners for better reliability
    setupAdvancedNotificationListeners();
    
    // Step 7: Schedule periodic token refresh
    scheduleTokenRefresh();
    
    log('✅ Messenger-style notifications initialized with lockscreen support', 'success');
    return { success: true };
  } catch (error) {
    log(`❌ Failed to initialize Messenger-style notifications: ${error.message}`, 'error');
    return { success: false, error: error.message };
  }
};

// Battery optimization checking and user instructions
export const checkBatteryOptimization = async () => {
  if (window.Capacitor && window.Capacitor.isNativePlatform()) {
    try {
      const { Device } = await import('@capacitor/device');
      
      // Check if battery optimization is enabled
      const batteryInfo = await Device.getBatteryInfo();
      
      // Log battery status for debugging
      log(`Battery level: ${batteryInfo.batteryLevel * 100}%`, 'info');
      
      // Show user instructions to disable battery optimization
      if (batteryInfo.batteryLevel < 0.2) {
        log('⚠️ Low battery - notifications may be limited', 'warning');
      }
      
      // Show user-friendly instructions
      showBatteryOptimizationInstructions();
    } catch (error) {
      log(`Error checking battery optimization: ${error.message}`, 'error');
    }
  }
};

const showBatteryOptimizationInstructions = () => {
  const instructions = `
🔋 To ensure you receive notifications reliably:

1. Go to Settings > Apps > YME PAD
2. Tap "Battery" or "Battery optimization"
3. Select "Don't optimize" or "Not optimized"
4. This prevents Android from killing the app in background

📱 Additional steps for some devices:
- Enable "Auto-start" or "Background activity"
- Disable "Power saving mode" for this app
- Add app to "Protected apps" list

🔒 For Lockscreen Notifications:
1. Go to Settings > Apps > YME PAD > Notifications
2. Enable "Show on lock screen"
3. Set notification importance to "High" or "Urgent"
4. Enable "Allow notification dot" and "Sound"
5. Disable "Silent notifications" if enabled

📳 Additional Lockscreen Settings:
- Go to Settings > Display > Lock screen
- Enable "Show notifications" or "Show all notification content"
- Make sure "Hide sensitive content" is disabled for this app
  `;
  
  log(instructions, 'info');
  
  // Store instructions for UI display
  if (typeof window !== 'undefined') {
    window.batteryOptimizationInstructions = instructions;
  }
};

// Setup advanced notification listeners for better reliability
export const setupAdvancedNotificationListeners = () => {
  try {
    log('Setting up advanced notification listeners...', 'info');
    
    // Listen for app state changes
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          log('App became visible - checking notification status', 'info');
          checkNotificationStatus();
        }
      });
    }
    
    // Listen for online/offline status
    window.addEventListener('online', () => {
      log('Device came online - refreshing notification token', 'info');
      const user = getLoggedInUser();
      if (user && user.uid) {
        refreshFCMToken(user.uid);
      }
    });
    
    window.addEventListener('offline', () => {
      log('Device went offline - notifications may be delayed', 'warning');
    });
    
    log('✅ Advanced notification listeners setup completed', 'success');
  } catch (error) {
    log(`Error setting up advanced listeners: ${error.message}`, 'error');
  }
};

// Schedule periodic token refresh
export const scheduleTokenRefresh = () => {
  try {
    log('Scheduling periodic token refresh...', 'info');
    
    // Refresh token every 6 hours
    const refreshInterval = setInterval(async () => {
      try {
        const user = getLoggedInUser();
        if (user && user.uid) {
          log('Performing scheduled token refresh...', 'info');
          await refreshFCMToken(user.uid);
        }
      } catch (error) {
        log(`Scheduled token refresh failed: ${error.message}`, 'error');
      }
    }, 6 * 60 * 60 * 1000); // 6 hours
    
    // Store interval ID for cleanup
    if (typeof window !== 'undefined') {
      window.notificationRefreshInterval = refreshInterval;
    }
    
    log('✅ Token refresh scheduled every 6 hours', 'success');
  } catch (error) {
    log(`Error scheduling token refresh: ${error.message}`, 'error');
  }
};

// Refresh FCM token
export const refreshFCMToken = async (userId) => {
  try {
    log('Refreshing FCM token...', 'info');
    
    const newToken = await getFCMToken(true); // Force refresh
    if (newToken) {
      await saveFCMToken(userId, newToken);
      log('✅ FCM token refreshed successfully', 'success');
      return true;
    } else {
      log('❌ Failed to get new FCM token', 'error');
      return false;
    }
  } catch (error) {
    log(`❌ Token refresh failed: ${error.message}`, 'error');
    return false;
  }
};

// Check notification status for debugging
export const checkNotificationStatus = () => {
  try {
    const status = {
      permission: 'Notification' in window ? Notification.permission : 'Not available',
      serviceWorker: 'serviceWorker' in navigator ? 'Available' : 'Not available',
      platform: window.Capacitor?.isNativePlatform() ? 'Native' : 'Web',
      timestamp: new Date().toISOString()
    };
    
    log(`Notification status check: ${JSON.stringify(status)}`, 'info');
    return status;
  } catch (error) {
    log(`Error checking notification status: ${error.message}`, 'error');
    return null;
  }
};

// Check lockscreen notification settings
export const checkLockscreenNotificationSettings = async () => {
  try {
    log('Checking lockscreen notification settings...', 'info');
    
    if (window.Capacitor && window.Capacitor.isNativePlatform()) {
      // For native platforms, we can't directly check lockscreen settings
      // But we can provide guidance
      log('📱 Native platform detected - lockscreen settings should be checked manually', 'info');
      
      const lockscreenInstructions = `
🔒 Lockscreen Notification Settings Check:

1. Go to Settings > Apps > YME PAD > Notifications
2. Verify these settings are enabled:
   ✅ Show on lock screen
   ✅ Allow notification dot
   ✅ Sound enabled
   ✅ Vibration enabled
   ✅ High importance/priority

3. Go to Settings > Display > Lock screen
4. Verify these settings:
   ✅ Show notifications
   ✅ Show all notification content (not "Hide sensitive content")

5. Go to Settings > Sound & vibration > Do Not Disturb
6. Make sure YME PAD is allowed to bypass DND
      `;
      
      log(lockscreenInstructions, 'info');
      
      // Store for UI display
      if (typeof window !== 'undefined') {
        window.lockscreenInstructions = lockscreenInstructions;
      }
    }
    
    return { success: true, message: 'Lockscreen settings check completed' };
  } catch (error) {
    log(`Error checking lockscreen settings: ${error.message}`, 'error');
    return { success: false, error: error.message };
  }
};

// Helper function to get logged in user (assuming it exists elsewhere)
const getLoggedInUser = () => {
  try {
    const userSession = localStorage.getItem('userSession');
    return userSession ? JSON.parse(userSession) : null;
  } catch (error) {
    log(`Error getting logged in user: ${error.message}`, 'error');
    return null;
  }
};
